import { SubmissionPlacementTaskInfo } from '@common/types';
import { groupBy } from 'lodash';
import { useEffect, useState } from 'react';
import { PlacementTaskStatus } from 'enums';
import { useMutateSubmissionPlacementTask, useToast } from 'hooks';
import { messages } from 'i18n';
import { extractErrorMessage } from 'utils';

interface UsePlacementTasksManagerProps {
  placementTasks: SubmissionPlacementTaskInfo[];
  onUpdateSuccess?: () => Promise<void>;
}

export function usePlacementTasksManager({ placementTasks, onUpdateSuccess }: UsePlacementTasksManagerProps) {
  const { updateStatus } = useMutateSubmissionPlacementTask();
  const { showToast } = useToast();

  // Local state for optimistic updates
  const [localPlacementTasks, setLocalPlacementTasks] = useState(placementTasks);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setLocalPlacementTasks(placementTasks);
  }, [placementTasks]);

  const getGroupingStatus = (task: SubmissionPlacementTaskInfo): PlacementTaskStatus => {
    if ([PlacementTaskStatus.Planned, PlacementTaskStatus.Skipped].includes(task.status)) {
      return PlacementTaskStatus.Planned;
    }
    return task.status;
  };

  const groupedTasks = groupBy(localPlacementTasks, getGroupingStatus) as Record<
    PlacementTaskStatus,
    SubmissionPlacementTaskInfo[]
  >;

  /**
   * Determines the next available status for a placement task based on workflow rules:
   * - Planned → InProgress (only first planned task, when no other task is in progress)
   * UPDATE: Planned → InProgress is currently disabled
   * - InProgress → Completed (always allowed)
   * - Completed/Skipped → No transition allowed
   */
  const getNextStatus = (task: SubmissionPlacementTaskInfo): PlacementTaskStatus | null => {
    const { status: taskStatus } = task;

    // const firstPlannedTask = localPlacementTasks.find((t) => t.status === PlacementTaskStatus.Planned);
    // const isFirstPlanned = firstPlannedTask?.id === taskId;
    //
    // const hasInProgressTask = localPlacementTasks.some(
    //   (t) => t.status === PlacementTaskStatus.InProgress && t.id !== taskId,
    // );
    //
    // if (taskStatus === PlacementTaskStatus.Planned) {
    //   return isFirstPlanned && !hasInProgressTask ? PlacementTaskStatus.InProgress : null;
    // }

    if (taskStatus === PlacementTaskStatus.InProgress) {
      return PlacementTaskStatus.Completed;
    }

    return null;
  };

  const showSkipButton = (task: SubmissionPlacementTaskInfo): boolean => {
    const { status: taskStatus, id: taskId } = task;

    if (taskStatus === PlacementTaskStatus.Planned) {
      return true;
    }

    // Can unskip only if no later tasks are in progress or completed
    if (taskStatus === PlacementTaskStatus.Skipped) {
      const currentTaskIndex = localPlacementTasks.findIndex((t) => t.id === taskId);
      if (currentTaskIndex === -1) {
        return false;
      }
      const laterTasks = localPlacementTasks.slice(currentTaskIndex + 1);
      const hasLaterProgressedTasks = laterTasks.some(
        (t) => t.status === PlacementTaskStatus.InProgress || t.status === PlacementTaskStatus.Completed,
      );
      return !hasLaterProgressedTasks;
    }

    return false;
  };

  const handleStatusChange = async (task: SubmissionPlacementTaskInfo, newStatus: PlacementTaskStatus) => {
    if (!task.id) {
      showToast('error', { message: messages.placementProcess.dialog.onMissingIdToast });
      return;
    }

    setIsUpdating(true);

    // Optimistically update the UI
    setLocalPlacementTasks((prevTasks) =>
      prevTasks.map((t) =>
        t.id === task.id
          ? {
              ...t,
              status: newStatus,
              // Set completedAt when moving to Completed
              ...(newStatus === PlacementTaskStatus.Completed && { completedAt: new Date() }),
            }
          : t,
      ),
    );

    try {
      await updateStatus.mutateAsync({
        id: task.id,
        data: { status: newStatus },
      });

      await onUpdateSuccess?.();

      showToast('success', { message: messages.placementProcess.dialog.onSuccessToast });
    } catch (error) {
      // Revert the optimistic update on error
      setLocalPlacementTasks(placementTasks);

      const errorMessage = extractErrorMessage(error);
      showToast('error', { message: messages.placementProcess.dialog.onErrorToast(errorMessage) });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSkip = async (task: SubmissionPlacementTaskInfo) => {
    if (!task.id) {
      showToast('error', { message: messages.placementProcess.dialog.onMissingIdToast });
      return;
    }

    const { status: currentStatus } = task;
    const newStatus =
      currentStatus === PlacementTaskStatus.Skipped ? PlacementTaskStatus.Planned : PlacementTaskStatus.Skipped;

    setIsUpdating(true);

    // Optimistically update the UI
    setLocalPlacementTasks((prevTasks) => prevTasks.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t)));

    try {
      await updateStatus.mutateAsync({
        id: task.id,
        data: { status: newStatus },
      });

      // Invalidate the submissions cache to refetch the data
      await onUpdateSuccess?.();

      showToast('success', {
        message:
          newStatus === PlacementTaskStatus.Skipped
            ? messages.placementProcess.dialog.taskSkipped
            : messages.placementProcess.dialog.taskUnskipped,
      });
    } catch (error) {
      // Revert the optimistic update on error
      setLocalPlacementTasks(placementTasks);

      const errorMessage = extractErrorMessage(error);
      showToast('error', { message: messages.placementProcess.dialog.onErrorToast(errorMessage) });
    } finally {
      setIsUpdating(false);
    }
  };

  return {
    localPlacementTasks,
    groupedTasks,
    isUpdating,
    getNextStatus,
    showSkipButton,
    handleStatusChange,
    handleSkip,
  };
}
